/**
 * Cliente para `gn-api-consolidate-service` (Sprint 1.2 / F2-02e).
 *
 * Este servico NAO e a porta de entrada do fluxo de propriedade (DP-1a):
 * o toggle "Atualizar dados" continua chamando `POST /api/v1/search` no
 * property-service, que delega internamente via
 * `integrations/consolidate_client.py` para o consolidate.
 *
 * O FE usa o consolidate APENAS para SSE:
 *   - `GET /events/status/{file_id}` (default event, payload em `data:`)
 *   - `GET /events/state/{file_id}`  (`event: state_update`)
 *
 * ### Auth no SSE (DP-2c)
 *
 * `EventSource` nativo do browser nao permite custom headers, entao
 * enviamos o JWT como query param `?token=...`. Backend precisa aceitar
 * esse esquema nos endpoints /events/*. Enquanto o backend nao exigir
 * auth, o query param simplesmente e ignorado (sem efeito colateral).
 *
 * ### Reconexao
 *
 * `EventSource` reconecta automaticamente em ~3s em caso de erro de
 * rede. O caller pode interromper via `close()` retornado aqui. Em
 * status terminal, o hook `usePropertyProcessingStream` fecha a
 * conexao para evitar reconexoes indesejadas.
 */

import { CONSOLIDATE_SERVICE_URL, getJwt } from './BaseApiService';
import {
  isTerminalPropertyProcessingStatus,
  TERMINAL_PROPERTY_PROCESSING_STATUSES,
} from '../types/events/consolidate';

const DEBUG = import.meta.env.VITE_DEBUG === 'true';

/** Resolve base URL: em DEV usa path relativo (proxy Vite /events). */
function resolveBaseUrl() {
  if (import.meta.env.DEV) return '';
  if (!CONSOLIDATE_SERVICE_URL) {
    console.warn(
      '[ConsolidateService] VITE_CONSOLIDATE_SERVICE_URL nao definido; SSE ficara inoperante em prod.',
    );
    return '';
  }
  return CONSOLIDATE_SERVICE_URL.replace(/\/+$/, '');
}

function buildEventUrl(path, { token } = {}) {
  const base = resolveBaseUrl();
  const url = `${base}${path}`;
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

/**
 * Abre EventSource para `/events/status/{file_id}`.
 *
 * @param {string} fileId
 * @param {object} opts
 * @param {(payload: import('../types/events/consolidate').PropertyStatusEvent) => void} [opts.onMessage]
 * @param {(ev: Event) => void} [opts.onError]
 * @param {() => void} [opts.onOpen]
 * @param {boolean} [opts.withAuth=true]
 * @returns {() => void} fecha a conexao quando chamado
 */
export function openStatusStream(fileId, opts = {}) {
  const { onMessage, onError, onOpen, withAuth = true } = opts;
  if (!fileId) {
    console.warn('[ConsolidateService] openStatusStream sem fileId');
    return () => {};
  }

  const token = withAuth ? getJwt() : null;
  const url = buildEventUrl(`/events/status/${encodeURIComponent(fileId)}`, {
    token,
  });

  if (DEBUG) console.log('[ConsolidateService] openStatusStream ->', url);

  const es = new EventSource(url, { withCredentials: false });

  es.onopen = () => {
    if (DEBUG) console.log('[ConsolidateService] SSE status open', fileId);
    if (typeof onOpen === 'function') onOpen();
  };

  es.onmessage = (ev) => {
    let payload;
    try {
      payload = JSON.parse(ev.data);
    } catch (err) {
      console.warn('[ConsolidateService] payload SSE invalido:', err, ev.data);
      return;
    }
    if (typeof onMessage === 'function') onMessage(payload);
  };

  es.onerror = (ev) => {
    if (DEBUG) console.warn('[ConsolidateService] SSE status error', ev);
    if (typeof onError === 'function') onError(ev);
  };

  return () => {
    try {
      es.close();
    } catch (_) {
      /* no-op */
    }
  };
}

/**
 * Abre EventSource para `/events/state/{file_id}`. Eventos sao enviados
 * com nome `state_update` pelo backend.
 *
 * @param {string} fileId
 * @param {object} opts
 * @param {(snapshot: import('../types/events/consolidate').PropertyStateUpdateEvent) => void} [opts.onStateUpdate]
 * @param {(ev: Event) => void} [opts.onError]
 * @param {boolean} [opts.withAuth=true]
 * @returns {() => void}
 */
export function openStateStream(fileId, opts = {}) {
  const { onStateUpdate, onError, withAuth = true } = opts;
  if (!fileId) {
    console.warn('[ConsolidateService] openStateStream sem fileId');
    return () => {};
  }

  const token = withAuth ? getJwt() : null;
  const url = buildEventUrl(`/events/state/${encodeURIComponent(fileId)}`, {
    token,
  });

  if (DEBUG) console.log('[ConsolidateService] openStateStream ->', url);

  const es = new EventSource(url, { withCredentials: false });

  es.addEventListener('state_update', (ev) => {
    let payload;
    try {
      payload = JSON.parse(ev.data);
    } catch (err) {
      console.warn('[ConsolidateService] state_update invalido:', err, ev.data);
      return;
    }
    if (typeof onStateUpdate === 'function') onStateUpdate(payload);
  });

  es.onerror = (ev) => {
    if (DEBUG) console.warn('[ConsolidateService] SSE state error', ev);
    if (typeof onError === 'function') onError(ev);
  };

  return () => {
    try {
      es.close();
    } catch (_) {
      /* no-op */
    }
  };
}

export { isTerminalPropertyProcessingStatus, TERMINAL_PROPERTY_PROCESSING_STATUSES };

export default {
  openStatusStream,
  openStateStream,
  isTerminalPropertyProcessingStatus,
};
